/**
 * Member Service
 *
 * Provides a clean abstraction layer between React components and Firebase.
 * All member CRUD operations go through this service.
 *
 * Benefits:
 * - Components don't need to know about Firebase internals
 * - Easy to mock for testing
 * - Centralized error handling
 * - Single place to add caching, retries, etc.
 */

import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { collection, doc, getDoc, getDocs, query, where, orderBy } from 'firebase/firestore';
import { functions, storage, db, auth } from '../firebase';

/**
 * @typedef {Object} Member
 * @property {string} id - Member document ID
 * @property {string} familyId - Parent family ID
 * @property {string} name - Member's first name
 * @property {string} surname - Member's surname (inherited from family)
 * @property {string} [email] - Email address
 * @property {string} [cellphone] - Cell phone
 * @property {string} [photo] - Photo storage path
 * @property {string} [birthdayMonth] - Birthday month (1-12)
 * @property {string} [birthdayDay] - Birthday day (1-31)
 * @property {string} [birthdayYear] - Birthday year
 * @property {Object} [imageUpdatedAt] - Timestamp for cache busting
 */

/**
 * @typedef {Object} CreateMemberInput
 * @property {string} familyId - Parent family ID (required)
 * @property {string} name - Member's first name (required)
 * @property {string} [email] - Email address
 * @property {string} [cellphone] - Cell phone
 * @property {string} [photo] - Photo storage path (from extracted faces)
 * @property {string} [birthdayMonth] - Birthday month (1-12)
 * @property {string} [birthdayDay] - Birthday day (1-31)
 * @property {string} [birthdayYear] - Birthday year
 */

/**
 * @typedef {Object} UpdateMemberInput
 * @property {string} memberId - Member ID (required)
 * @property {string} [name] - Updated name
 * @property {string} [email] - Updated email
 * @property {string} [cellphone] - Updated cellphone
 * @property {string} [photo] - Updated photo path
 * @property {string} [birthdayMonth] - Updated birthday month
 * @property {string} [birthdayDay] - Updated birthday day
 * @property {string} [birthdayYear] - Updated birthday year
 */

/**
 * @typedef {Object} MemberServiceResult
 * @property {boolean} success - Whether the operation succeeded
 * @property {string} [memberId] - Member ID (for create operations)
 * @property {string} [message] - Success/error message
 * @property {string} [error] - Error message if failed
 */

// Callable function references
const addMemberV2 = httpsCallable(functions, 'addMemberV2');
const updateMemberV2 = httpsCallable(functions, 'updateMemberV2');
const deleteMemberFn = httpsCallable(functions, 'deleteMember');

/**
 * Get the current user's organization ID from claims
 * @returns {Promise<string>} The organization ID
 * @throws {Error} If user is not authenticated or missing orgId claim
 */
async function getOrgId() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const idTokenResult = await user.getIdTokenResult();
  const orgId = idTokenResult.claims.orgId;

  if (!orgId) {
    throw new Error('Organization ID not found in user claims. Please re-login.');
  }

  return orgId;
}

/**
 * Get the download URL for a member photo
 * @param {string} photoPath - The storage path
 * @param {Object} [imageUpdatedAt] - Timestamp for cache busting
 * @returns {Promise<string|null>} The download URL or null
 */
async function getMemberPhotoUrl(photoPath, imageUpdatedAt) {
  if (!photoPath) {
    return null;
  }

  try {
    const photoRef = ref(storage, photoPath);
    let url = await getDownloadURL(photoRef);

    // Add cache-busting parameter if we have a timestamp
    if (imageUpdatedAt) {
      const timestamp = imageUpdatedAt.seconds || imageUpdatedAt._seconds || Date.now() / 1000;
      url = `${url}&t=${timestamp}`;
    }

    return url;
  } catch (error) {
    console.error('Error getting member photo URL:', error);
    return null;
  }
}

/**
 * Create a new member
 * @param {CreateMemberInput} input - Member data
 * @returns {Promise<MemberServiceResult>} Result with memberId
 */
async function createMember(input) {
  try {
    // Prepare data for Cloud Function
    const memberData = {
      familyId: input.familyId,
      name: input.name?.trim() || '',
      email: input.email?.trim() || null,
      cellphone: input.cellphone?.trim() || null,
      photo: input.photo || null,
      birthdayMonth: input.birthdayMonth ? String(input.birthdayMonth) : null,
      birthdayDay: input.birthdayDay ? String(input.birthdayDay) : null,
      birthdayYear: input.birthdayYear ? String(input.birthdayYear) : null,
    };

    const result = await addMemberV2(memberData);

    if (result.data?.success) {
      return {
        success: true,
        memberId: result.data.memberId,
        message: result.data.message || 'Member created successfully',
      };
    }

    throw new Error(result.data?.message || 'Failed to create member');
  } catch (error) {
    console.error('Error creating member:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Update an existing member
 * @param {UpdateMemberInput} input - Updated member data
 * @returns {Promise<MemberServiceResult>} Result
 */
async function updateMember(input) {
  try {
    // Prepare update payload (only include fields that are being updated)
    const updateData = {
      memberId: input.memberId,
    };

    if (input.name !== undefined) updateData.name = input.name?.trim();
    if (input.email !== undefined) updateData.email = input.email?.trim() || null;
    if (input.cellphone !== undefined) updateData.cellphone = input.cellphone?.trim() || null;
    if (input.photo !== undefined) updateData.photo = input.photo || null;
    if (input.birthdayMonth !== undefined) {
      updateData.birthdayMonth = input.birthdayMonth ? String(input.birthdayMonth) : null;
    }
    if (input.birthdayDay !== undefined) {
      updateData.birthdayDay = input.birthdayDay ? String(input.birthdayDay) : null;
    }
    if (input.birthdayYear !== undefined) {
      updateData.birthdayYear = input.birthdayYear ? String(input.birthdayYear) : null;
    }

    const result = await updateMemberV2(updateData);

    if (result.data?.success) {
      return {
        success: true,
        message: result.data.message || 'Member updated successfully',
      };
    }

    throw new Error(result.data?.message || 'Failed to update member');
  } catch (error) {
    console.error('Error updating member:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Delete a member
 * @param {string} memberId - Member ID to delete
 * @returns {Promise<MemberServiceResult>} Result
 */
async function deleteMember(memberId) {
  try {
    const result = await deleteMemberFn({ memberId });

    if (result.data?.success) {
      return {
        success: true,
        message: result.data.message || 'Member deleted successfully',
      };
    }

    throw new Error(result.data?.message || 'Failed to delete member');
  } catch (error) {
    console.error('Error deleting member:', error);
    return {
      success: false,
      error: extractErrorMessage(error),
    };
  }
}

/**
 * Get a single member by ID
 * @param {string} memberId - Member ID
 * @returns {Promise<Member|null>} The member or null if not found
 */
async function getMember(memberId) {
  try {
    const orgId = await getOrgId();
    const memberDoc = await getDoc(doc(db, 'member', memberId));

    if (!memberDoc.exists()) {
      return null;
    }

    const data = memberDoc.data();

    // Verify org isolation
    if (data.orgId !== orgId) {
      console.warn('Attempted to access member from different org');
      return null;
    }

    return {
      ...data,
      id: memberDoc.id,  // Put after spread so document's id:null doesn't overwrite
    };
  } catch (error) {
    console.error('Error fetching member:', error);
    throw error;
  }
}

/**
 * Get all members for a family
 * @param {string} familyId - Family ID
 * @param {Object} [options] - Query options
 * @param {string} [options.orderByField='name'] - Field to order by
 * @returns {Promise<Member[]>} Array of members
 */
async function getMembersByFamily(familyId, options = {}) {
  const { orderByField = 'name' } = options;

  try {
    const orgId = await getOrgId();

    const membersQuery = query(
      collection(db, 'member'),
      where('familyId', '==', familyId),
      where('orgId', '==', orgId),
      orderBy(orderByField)
    );

    const snapshot = await getDocs(membersQuery);
    const members = [];

    snapshot.forEach((doc) => {
      members.push({
        ...doc.data(),
        id: doc.id,  // Put after spread so document's id:null doesn't overwrite
      });
    });

    return members;
  } catch (error) {
    console.error('Error fetching members:', error);
    throw error;
  }
}

/**
 * Get all members for the current organization
 * @param {Object} [options] - Query options
 * @param {string} [options.orderByField='name'] - Field to order by
 * @returns {Promise<Member[]>} Array of all members
 */
async function getAllMembers(options = {}) {
  const { orderByField = 'name' } = options;

  try {
    const orgId = await getOrgId();

    const membersQuery = query(
      collection(db, 'member'),
      where('orgId', '==', orgId),
      orderBy(orderByField)
    );

    const snapshot = await getDocs(membersQuery);
    const members = [];

    snapshot.forEach((doc) => {
      members.push({
        ...doc.data(),
        id: doc.id,  // Put after spread so document's id:null doesn't overwrite
      });
    });

    return members;
  } catch (error) {
    console.error('Error fetching all members:', error);
    throw error;
  }
}

/**
 * Extract user-friendly error message from various error types
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
function extractErrorMessage(error) {
  // Firebase Functions error
  if (error.code && error.message) {
    return error.message;
  }

  // Standard Error
  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
}

// Export the service as a single object
const memberService = {
  createMember,
  updateMember,
  deleteMember,
  getMember,
  getMembersByFamily,
  getAllMembers,
  getMemberPhotoUrl,
  getOrgId,
};

export default memberService;

// Also export individual functions for tree-shaking
export {
  createMember,
  updateMember,
  deleteMember,
  getMember,
  getMembersByFamily,
  getAllMembers,
  getMemberPhotoUrl,
  getOrgId,
  extractErrorMessage,
};
